import React, { useEffect, useState, useRef } from "react";
import { ProjectAPI } from "../api/apiClient";
import { useUI } from "../context/UIContext";

// ------------------------------------
// Issue Type
// ------------------------------------
type Issue = {
  taskId: number;
  title: string;
  dueDate?: string;
  status?: string;
  taskGroupName?: string;
};

// ------------------------------------
// Issue Section Component
// ------------------------------------
function IssueSection({
  title,
  items,
  onDetails,
  onChat,
}: {
  title: string;
  items: Issue[];
  onDetails: (issue: Issue) => void;
  onChat: (issue: Issue) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="mb-8">
      {/* Section Heading */}
      <div className="flex items-center gap-2 mb-3 border-b border-gray-300 pb-2">
        <h3 className="text-lg font-semibold text-gray-700">
          {title}
        </h3>

        <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-1">
          {items.length}
        </span>
      </div>

      {/* Issues Under Section */}
      <div className="space-y-3">
        {items.map((issue) => (
          <div
            key={issue.taskId}
            className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 shadow-sm"
          >
            {/* Issue Title */}
            <div className="font-semibold text-gray-800 mb-1">
              {issue.title}
            </div>

            {/* Task Group */}
            {issue.taskGroupName && (
              <div className="text-sm text-gray-500 mb-1">
                Task Group: {issue.taskGroupName}
              </div>
            )}

            {/* Due Date */}
            <div className="text-xs text-gray-500 mb-3">
              Due Date:{" "}
              {issue.dueDate
                ? new Date(issue.dueDate).toLocaleDateString()
                : "No date"}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {/* Details Button */}
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => onDetails(issue)}
              >
                📄 Details
              </button>

              {/* Chat Button */}
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800"
                onClick={() => onChat(issue)}
              >
                💬 Chat
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------
// Main All Issues Component
// ------------------------------------
export default function AllIssues() {
  const {
    selectedProjectId,
    setSelectedIssueId,
    setSelectedIssueTitle,
    setViewMode,
    pushUIState,
    allIssuesFilters,
    setAllIssuesFilters,
  } = useUI();

  const { status, taskGroup, fromDue, toDue } =
    allIssuesFilters ?? {
      status: "",
      taskGroup: "",
      fromDue: "",
      toDue: "",
    };

  const [issues, setIssues] = useState<Issue[]>([]);

  const [taskGroups, setTaskGroups] = useState<
    {
      taskGroupNo: number;
      taskGroupName: string;
    }[]
  >([]);

  const hasPushedBase = useRef(false);

  // ------------------------------------
  // Preserve UI Navigation State
  // ------------------------------------
  useEffect(() => {
    if (hasPushedBase.current) return;

    pushUIState();
    hasPushedBase.current = true;
  }, [pushUIState]);

  // ------------------------------------
  // Load Task Groups
  // ------------------------------------
  useEffect(() => {
    if (!selectedProjectId) return;

    ProjectAPI.getTaskGroups(selectedProjectId)
      .then((data) => {
        setTaskGroups(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Error loading task groups:", error);
        setTaskGroups([]);
      });
  }, [selectedProjectId]);

  // ------------------------------------
  // Load Issues
  // ------------------------------------
  useEffect(() => {
    if (!selectedProjectId) return;

    loadIssues(selectedProjectId);
  }, [
    selectedProjectId,
    status,
    taskGroup,
    fromDue,
    toDue,
  ]);

  async function loadIssues(projectId: number) {
    try {
      const params = new URLSearchParams();

      if (status) {
        params.append("status", status);
      }

      if (taskGroup) {
        params.append("taskGroup", taskGroup);
      }

      if (fromDue) {
        params.append("fromDue", fromDue);
      }

      if (toDue) {
        params.append("toDue", toDue);
      }

      const data = await ProjectAPI.getAllIssues(
        projectId,
        params
      );

      setIssues(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading all issues:", error);
      setIssues([]);
    }
  }

  // ------------------------------------
  // Group Issues by Status
  // ------------------------------------
  const openIssues = issues.filter(
    (issue) =>
      issue.status?.toLowerCase() === "open"
  );

  const holdIssues = issues.filter(
    (issue) =>
      issue.status?.toLowerCase() === "hold"
  );

  const closedIssues = issues.filter(
    (issue) =>
      issue.status?.toLowerCase() === "closed"
  );

  // ------------------------------------
  // Open Issue Details
  // ------------------------------------
  function openDetails(issue: Issue) {
    pushUIState();

    setSelectedIssueId(issue.taskId);
    setSelectedIssueTitle(issue.title);
    setViewMode("issue-details");
  }

  // ------------------------------------
  // Open Issue Chat
  // ------------------------------------
  function openChat(issue: Issue) {
    pushUIState();

    setSelectedIssueId(issue.taskId);
    setSelectedIssueTitle(issue.title);
    setViewMode("issue-chat");
  }

  // ------------------------------------
  // Render
  // ------------------------------------
  return (
    <div className="p-4">
      {/* Page Title */}
      <h2 className="text-xl font-bold mb-4">
        All Issues
      </h2>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {/* Status Filter */}
        <select
          value={status}
          onChange={(e) =>
            setAllIssuesFilters((previous) => ({
              ...previous,
              status: e.target.value,
            }))
          }
          className="border border-gray-300 p-2 rounded"
        >
          <option value="">All Statuses</option>
          <option value="Open">Open</option>
          <option value="Hold">Hold</option>
          <option value="Closed">Closed</option>
        </select>

        {/* Task Group Filter */}
        <select
          value={taskGroup}
          onChange={(e) =>
            setAllIssuesFilters((previous) => ({
              ...previous,
              taskGroup: e.target.value,
            }))
          }
          className="border border-gray-300 p-2 rounded"
        >
          <option value="">All Task Groups</option>

          {taskGroups.map((group) => (
            <option
              key={group.taskGroupNo}
              value={group.taskGroupNo}
            >
              {group.taskGroupName}
            </option>
          ))}
        </select>

        {/* From Due Date */}
        <input
          type="date"
          value={fromDue}
          onChange={(e) =>
            setAllIssuesFilters((previous) => ({
              ...previous,
              fromDue: e.target.value,
            }))
          }
          className="border border-gray-300 p-2 rounded"
        />

        {/* To Due Date */}
        <input
          type="date"
          value={toDue}
          onChange={(e) =>
            setAllIssuesFilters((previous) => ({
              ...previous,
              toDue: e.target.value,
            }))
          }
          className="border border-gray-300 p-2 rounded"
        />
      </div>

      {/* Open Issues */}
      <IssueSection
        title="Open"
        items={openIssues}
        onDetails={openDetails}
        onChat={openChat}
      />

      {/* Hold Issues */}
      <IssueSection
        title="Holded"
        items={holdIssues}
        onDetails={openDetails}
        onChat={openChat}
      />

      {/* Closed Issues */}
      <IssueSection
        title="Closed"
        items={closedIssues}
        onDetails={openDetails}
        onChat={openChat}
      />

      {/* No Issues */}
      {openIssues.length === 0 &&
        holdIssues.length === 0 &&
        closedIssues.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            No issues found.
          </div>
        )}
    </div>
  );
}